import { PropsWithChildren, useCallback, useMemo, useRef } from 'react';

import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { useReportError } from '@/hooks/useReportError';
import { useTrowser } from '@/modules/navigation/trowsers/trowser.context';
import { TrowserKey } from '@/modules/navigation/trowsers/trowser.types';

import { PaneForms, useStepCommit } from '../hooks/useStepCommit';
import {
  MANAGE_INTEGRATION_DATA_STEP_ORDER,
  ManageIntegrationDataPane,
  ManageIntegrationDataStep,
  PANE_ORDER,
  parseManageIntegrationDataPane,
  STEP_BY_PANE,
} from '../ManageIntegrationDataModal.types';
import { useCashflowsImportForm } from '../steps/importData/hooks/useCashflowsImportForm';
import { useEntitiesImportForm } from '../steps/importData/hooks/useEntitiesImportForm';
import { useLiabilitiesImportForm } from '../steps/importData/hooks/useLiabilitiesImportForm';
import { useLifeInsuranceImportForm } from '../steps/importData/hooks/useLifeInsuranceImportForm';
import { useEntityLinkForm } from '../steps/linkData/hooks/useEntityLinkForm';
import { useSelectClientsForm } from '../steps/selectClients/useSelectClientsForm';
import {
  ManageIntegrationDataContext,
  ManageIntegrationDataContextShape,
} from './manageIntegrationData.context';

export interface ManageIntegrationDataProviderProps {
  householdId: string;
  onClose: () => void;
  /** Pane to open on, e.g. from a deep link. Defaults to the first step. */
  initialPane?: ManageIntegrationDataPane;
}

function firstPaneOfStep(
  step: ManageIntegrationDataStep
): ManageIntegrationDataPane {
  const pane = PANE_ORDER.find((candidate) => STEP_BY_PANE[candidate] === step);
  // Every step owns at least one pane, so this can't be missed.
  return pane ?? ManageIntegrationDataPane.SelectClients;
}

function useContextValue({
  householdId,
  onClose,
  initialPane = ManageIntegrationDataPane.SelectClients,
}: ManageIntegrationDataProviderProps): ManageIntegrationDataContextShape {
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();
  // Successful saves remain relevant after forms clear their selections,
  // including when another pane fails and the user retries Done.
  const hasImportedRecords = useRef(false);
  const onImported = useCallback(() => {
    hasImportedRecords.current = true;
  }, []);

  // The visible pane lives in the trowser's `section`, so a reload or a shared
  // link resumes on it.
  const { section, setSection } = useTrowser(TrowserKey.LinkHouseholdData);
  const activePane = parseManageIntegrationDataPane(section) ?? initialPane;
  const currentStep = STEP_BY_PANE[activePane];
  const activeStepIndex =
    MANAGE_INTEGRATION_DATA_STEP_ORDER.indexOf(currentStep);

  const goToPane = useCallback(
    (pane: ManageIntegrationDataPane) => setSection(pane),
    [setSection]
  );

  // The provider owns each pane's form so values outlive the pane unmounting.
  const selectClientsForm = useSelectClientsForm(
    householdId,
    undefined,
    onImported
  );
  const directHoldingsLinkForm = useEntityLinkForm(
    'direct holdings',
    onImported
  );
  const trustsBusinessesLinkForm = useEntityLinkForm(
    'trusts and businesses',
    onImported
  );

  // Read at save time, so the answer tracks the link panes' live form state.
  const getClaimedIntegrationEntityIds = useCallback(
    () =>
      new Set([
        ...directHoldingsLinkForm.getClaimedIntegrationEntityIds(),
        ...trustsBusinessesLinkForm.getClaimedIntegrationEntityIds(),
      ]),
    [directHoldingsLinkForm, trustsBusinessesLinkForm]
  );

  const entitiesImportForm = useEntitiesImportForm(
    getClaimedIntegrationEntityIds,
    onImported
  );
  const liabilitiesImportForm = useLiabilitiesImportForm(
    householdId,
    onImported
  );
  const lifeInsuranceImportForm = useLifeInsuranceImportForm(
    householdId,
    onImported
  );
  const cashflowsImportForm = useCashflowsImportForm(householdId, onImported);

  const paneForms: PaneForms = useMemo(
    () => ({
      [ManageIntegrationDataPane.SelectClients]: selectClientsForm,
      [ManageIntegrationDataPane.LinkTrusts]: trustsBusinessesLinkForm,
      [ManageIntegrationDataPane.LinkHoldings]: directHoldingsLinkForm,
      [ManageIntegrationDataPane.ImportDataEntities]: entitiesImportForm,
      [ManageIntegrationDataPane.ImportDataLiabilities]: liabilitiesImportForm,
      [ManageIntegrationDataPane.ImportDataLifeInsurance]:
        lifeInsuranceImportForm,
      [ManageIntegrationDataPane.ImportDataCashflows]: cashflowsImportForm,
    }),
    [
      selectClientsForm,
      directHoldingsLinkForm,
      trustsBusinessesLinkForm,
      entitiesImportForm,
      liabilitiesImportForm,
      lifeInsuranceImportForm,
      cashflowsImportForm,
    ]
  );

  const { isSaving, commitPanes, requestClose } = useStepCommit({
    paneForms,
    onPaneBlocked: goToPane,
    onPaneFailure: (pane, error) => {
      reportError(`Failed to save ${pane}`, error as Error);
      goToPane(pane);
    },
  });

  const panesOfCurrentStep = useMemo(
    () => PANE_ORDER.filter((pane) => STEP_BY_PANE[pane] === currentStep),
    [currentStep]
  );

  const goToStepIndex = useCallback(
    (index: number) => {
      void (async () => {
        const nextStep = MANAGE_INTEGRATION_DATA_STEP_ORDER[index];
        if (!nextStep || nextStep === currentStep) return;

        if (index > activeStepIndex) {
          // Include skipped steps. A user can edit a later step, navigate back,
          // and then use the tracker to jump forward past those pending edits.
          const panesToCommit = PANE_ORDER.filter((pane) => {
            const paneStepIndex = MANAGE_INTEGRATION_DATA_STEP_ORDER.indexOf(
              STEP_BY_PANE[pane]
            );
            return paneStepIndex >= activeStepIndex && paneStepIndex < index;
          });
          if (!(await commitPanes(panesToCommit))) return;
        }

        setSection(firstPaneOfStep(nextStep));
      })();
    },
    [activeStepIndex, currentStep, commitPanes, setSection]
  );

  const closeModal = useCallback(
    () => requestClose(onClose),
    [requestClose, onClose]
  );

  // The whole step rather than just the visible tab, so switching tabs before
  // pressing Done can't quietly drop what was entered on the other one.
  const commitAndClose = useCallback(() => {
    void (async () => {
      if (await commitPanes(panesOfCurrentStep)) {
        if (hasImportedRecords.current) {
          showFeedback('Integration data updated', { variant: 'success' });
        } else {
          showFeedback('No integration data updated.', {
            variant: 'info-low',
          });
        }
        onClose();
      }
    })();
  }, [onClose, panesOfCurrentStep, commitPanes, showFeedback]);

  const goToPreviousStep = useCallback(
    () => goToStepIndex(activeStepIndex - 1),
    [activeStepIndex, goToStepIndex]
  );

  const goToNextStep = useCallback(
    () => goToStepIndex(activeStepIndex + 1),
    [activeStepIndex, goToStepIndex]
  );

  return {
    householdId,
    activePane,
    currentStep,
    activeStepIndex,
    isFirstStep: activeStepIndex === 0,
    isLastStep:
      activeStepIndex === MANAGE_INTEGRATION_DATA_STEP_ORDER.length - 1,
    isSaving,
    goToPane,
    goToStepIndex,
    goToPreviousStep,
    goToNextStep,
    commitAndClose,
    closeModal,
    selectClientsForm,
    directHoldingsLinkForm,
    trustsBusinessesLinkForm,
    entitiesImportForm,
    liabilitiesImportForm,
    lifeInsuranceImportForm,
    cashflowsImportForm,
  };
}

export function ManageIntegrationDataProvider({
  children,
  ...props
}: PropsWithChildren<ManageIntegrationDataProviderProps>) {
  const value = useContextValue(props);

  return (
    <ManageIntegrationDataContext.Provider value={value}>
      {children}
    </ManageIntegrationDataContext.Provider>
  );
}
